import type { AppointmentBrowserContractMarker } from '@/features/appointments/browserCapability';
import type {
  DecodedBrowserAppointment,
  ParticipantAppointment,
  ParticipantAppointmentDetail,
} from '@/features/appointments/types';
import type { ParticipantAppointmentTransport } from '@/store/api/momentsParticipantAppointmentApi';

import { notifyReceiptOwner } from '@/features/appointments/receiptOwnerChannel';

export const BOOKING_ATTEMPT_STORAGE_KEY =
  'member-biweekly-booking:current-attempt';
const MAX_STORED_BOOKING_ATTEMPTS = 4;

export interface BookingConfirmationCandidate {
  appointmentIds: string[];
  cadenceWeeks: number;
  calendarEventTitle?: string;
  coach: {
    avatarUrl: string | null;
    displayName: string;
    personId: string;
  };
  durationMinutes?: number;
  firstStart: string;
  memberEmail: string;
  occurrenceCount: number;
  seatType: 'executive' | 'leadership';
  seriesId: string;
  timezone: string;
}

export interface BookingConfirmationExpectation {
  cadenceWeeks: number;
  coachPersonId: string;
  firstStart: string;
  memberPersonId: string;
  occurrenceCount: number;
  seatType: 'executive' | 'leadership';
}

interface StoredBookingAttemptBase {
  bookContractVersion?: 'appointment-book.v2';
  browserContractMarker?: AppointmentBrowserContractMarker;
  calendarEventTitle?: string;
  canonicalDigest: string;
  confirmationCandidate: BookingConfirmationCandidate | null;
  confirmationExpectation?: BookingConfirmationExpectation;
  confirmationInvalidated: boolean;
  errorMessage?: string | null;
  generation: number;
  idempotencyKey: string;
  pendingAppointmentIds: string[];
  pendingOperationId: string | null;
  rejectedCanonicalDigest: string | null;
  retryAfterSeconds?: number | null;
  routeKey: string;
  status:
    | 'confirmed'
    | 'error'
    | 'pending'
    | 'reconciling'
    | 'rejected'
    | 'submitting';
  transport?: ParticipantAppointmentTransport;
}

export interface StoredBookingAttempt extends StoredBookingAttemptBase {}

type RecoverableStoredBookingAttempt = Omit<
  StoredBookingAttemptBase,
  | 'confirmationCandidate'
  | 'confirmationInvalidated'
  | 'pendingAppointmentIds'
  | 'pendingOperationId'
  | 'rejectedCanonicalDigest'
  | 'status'
> & {
  confirmationCandidate: null;
  confirmationInvalidated: false;
  pendingAppointmentIds: [];
  pendingOperationId: null;
  rejectedCanonicalDigest: null;
  status: 'error' | 'reconciling' | 'submitting';
};

type OwnedErrorStoredBookingAttempt = Omit<
  StoredBookingAttemptBase,
  | 'confirmationCandidate'
  | 'confirmationInvalidated'
  | 'errorMessage'
  | 'pendingOperationId'
  | 'rejectedCanonicalDigest'
  | 'status'
> & {
  confirmationCandidate: null;
  confirmationInvalidated: false;
  errorMessage: string;
  pendingOperationId: string;
  rejectedCanonicalDigest: null;
  status: 'error';
};

type PendingStoredBookingAttempt = Omit<
  StoredBookingAttemptBase,
  | 'confirmationCandidate'
  | 'confirmationInvalidated'
  | 'pendingOperationId'
  | 'rejectedCanonicalDigest'
  | 'status'
> & {
  confirmationCandidate: BookingConfirmationCandidate | null;
  confirmationInvalidated: false;
  pendingOperationId: string;
  rejectedCanonicalDigest: null;
  status: 'pending';
};

type ConfirmedStoredBookingAttempt = Omit<
  StoredBookingAttemptBase,
  | 'confirmationCandidate'
  | 'pendingOperationId'
  | 'rejectedCanonicalDigest'
  | 'status'
> & {
  confirmationCandidate: BookingConfirmationCandidate;
  pendingOperationId: string | null;
  rejectedCanonicalDigest: null;
  status: 'confirmed';
};

type RejectedStoredBookingAttempt = Omit<
  StoredBookingAttemptBase,
  | 'confirmationCandidate'
  | 'confirmationInvalidated'
  | 'pendingOperationId'
  | 'rejectedCanonicalDigest'
  | 'status'
> & {
  confirmationCandidate: BookingConfirmationCandidate | null;
  confirmationInvalidated: false;
  pendingOperationId: string;
  rejectedCanonicalDigest: string;
  status: 'rejected';
};

type ValidatedStoredBookingAttempt =
  | ConfirmedStoredBookingAttempt
  | OwnedErrorStoredBookingAttempt
  | PendingStoredBookingAttempt
  | RecoverableStoredBookingAttempt
  | RejectedStoredBookingAttempt;

const isConfirmationCandidate = (
  value: unknown,
): value is BookingConfirmationCandidate => {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Partial<BookingConfirmationCandidate>;
  const coach = candidate.coach as
    Partial<BookingConfirmationCandidate['coach']> | undefined;
  return (
    Array.isArray(candidate.appointmentIds) &&
    candidate.appointmentIds.every((id) => typeof id === 'string') &&
    typeof candidate.cadenceWeeks === 'number' &&
    candidate.cadenceWeeks > 0 &&
    (candidate.calendarEventTitle === undefined ||
      (typeof candidate.calendarEventTitle === 'string' &&
        candidate.calendarEventTitle.length > 0 &&
        Array.from(candidate.calendarEventTitle).length <= 1024)) &&
    coach !== undefined &&
    (coach.avatarUrl === null || typeof coach.avatarUrl === 'string') &&
    typeof coach.displayName === 'string' &&
    typeof coach.personId === 'string' &&
    (candidate.durationMinutes === undefined ||
      (typeof candidate.durationMinutes === 'number' &&
        candidate.durationMinutes > 0)) &&
    typeof candidate.firstStart === 'string' &&
    typeof candidate.memberEmail === 'string' &&
    typeof candidate.occurrenceCount === 'number' &&
    candidate.occurrenceCount > 0 &&
    (candidate.seatType === 'executive' ||
      candidate.seatType === 'leadership') &&
    typeof candidate.seriesId === 'string' &&
    typeof candidate.timezone === 'string'
  );
};

const isStoredBookingAttempt = (
  value: unknown,
  // eslint-disable-next-line complexity -- Storage parsing validates every field and status invariant at one trust boundary.
): value is ValidatedStoredBookingAttempt => {
  if (value === null || typeof value !== 'object') return false;
  const attempt = value as Partial<StoredBookingAttempt>;
  const { transport } = value as { transport?: unknown };
  const expectation = attempt.confirmationExpectation as
    Partial<BookingConfirmationExpectation> | undefined;
  const structurallyValid =
    ((attempt.bookContractVersion === undefined &&
      attempt.browserContractMarker === undefined &&
      attempt.calendarEventTitle === undefined) ||
      (attempt.bookContractVersion === 'appointment-book.v2' &&
        attempt.browserContractMarker ===
          'browser-appointments.biweekly-booking-details-v2' &&
        typeof attempt.calendarEventTitle === 'string' &&
        attempt.calendarEventTitle.length > 0 &&
        Array.from(attempt.calendarEventTitle).length <= 1024)) &&
    (transport === undefined ||
      transport === 'rails' ||
      transport === 'moments') &&
    (expectation === undefined ||
      (typeof expectation.cadenceWeeks === 'number' &&
        expectation.cadenceWeeks > 0 &&
        typeof expectation.coachPersonId === 'string' &&
        expectation.coachPersonId.length > 0 &&
        typeof expectation.firstStart === 'string' &&
        Number.isFinite(Date.parse(expectation.firstStart)) &&
        typeof expectation.memberPersonId === 'string' &&
        expectation.memberPersonId.length > 0 &&
        typeof expectation.occurrenceCount === 'number' &&
        Number.isInteger(expectation.occurrenceCount) &&
        expectation.occurrenceCount > 0 &&
        (expectation.seatType === 'executive' ||
          expectation.seatType === 'leadership'))) &&
    typeof attempt.canonicalDigest === 'string' &&
    attempt.canonicalDigest.length > 0 &&
    typeof attempt.confirmationInvalidated === 'boolean' &&
    typeof attempt.generation === 'number' &&
    Number.isInteger(attempt.generation) &&
    attempt.generation >= 0 &&
    typeof attempt.idempotencyKey === 'string' &&
    attempt.idempotencyKey.length > 0 &&
    (attempt.errorMessage === undefined ||
      attempt.errorMessage === null ||
      (typeof attempt.errorMessage === 'string' &&
        attempt.errorMessage.length > 0)) &&
    (attempt.confirmationCandidate === null ||
      isConfirmationCandidate(attempt.confirmationCandidate)) &&
    Array.isArray(attempt.pendingAppointmentIds) &&
    attempt.pendingAppointmentIds.every(
      (id) => typeof id === 'string' && id.length > 0,
    ) &&
    new Set(attempt.pendingAppointmentIds).size ===
      attempt.pendingAppointmentIds.length &&
    (attempt.pendingOperationId === null ||
      (typeof attempt.pendingOperationId === 'string' &&
        attempt.pendingOperationId.length > 0)) &&
    (attempt.rejectedCanonicalDigest === null ||
      (typeof attempt.rejectedCanonicalDigest === 'string' &&
        attempt.rejectedCanonicalDigest.length > 0)) &&
    (attempt.retryAfterSeconds === undefined ||
      attempt.retryAfterSeconds === null ||
      (typeof attempt.retryAfterSeconds === 'number' &&
        Number.isInteger(attempt.retryAfterSeconds) &&
        attempt.retryAfterSeconds > 0)) &&
    typeof attempt.routeKey === 'string' &&
    attempt.routeKey.length > 0 &&
    [
      'confirmed',
      'error',
      'pending',
      'reconciling',
      'rejected',
      'submitting',
    ].includes(attempt.status ?? '');
  if (!structurallyValid) return false;

  const validAttempt = attempt as StoredBookingAttempt;

  const candidateIdsMatch =
    validAttempt.confirmationCandidate !== null &&
    validAttempt.pendingAppointmentIds.length > 0 &&
    validAttempt.pendingAppointmentIds.length ===
      validAttempt.confirmationCandidate.appointmentIds.length &&
    validAttempt.pendingAppointmentIds.every(
      (id, index) =>
        id === validAttempt.confirmationCandidate?.appointmentIds[index],
    );

  switch (validAttempt.status) {
    case 'pending':
      return (
        validAttempt.confirmationInvalidated === false &&
        (validAttempt.errorMessage === undefined ||
          validAttempt.errorMessage === null) &&
        validAttempt.pendingOperationId !== null &&
        validAttempt.rejectedCanonicalDigest === null &&
        (candidateIdsMatch ||
          (validAttempt.transport === 'moments' &&
            validAttempt.confirmationCandidate === null &&
            validAttempt.confirmationExpectation !== undefined &&
            validAttempt.pendingAppointmentIds.length === 0))
      );
    case 'confirmed':
      return (
        validAttempt.confirmationCandidate !== null &&
        (validAttempt.errorMessage === undefined ||
          validAttempt.errorMessage === null) &&
        validAttempt.pendingOperationId !== null &&
        validAttempt.rejectedCanonicalDigest === null &&
        candidateIdsMatch
      );
    case 'rejected':
      return (
        validAttempt.confirmationInvalidated === false &&
        (validAttempt.errorMessage === undefined ||
          typeof validAttempt.errorMessage === 'string') &&
        validAttempt.pendingOperationId !== null &&
        validAttempt.rejectedCanonicalDigest === validAttempt.canonicalDigest &&
        (validAttempt.confirmationCandidate === null
          ? validAttempt.pendingAppointmentIds.length === 0
          : candidateIdsMatch)
      );
    case 'error':
      if (validAttempt.pendingOperationId !== null) {
        return (
          validAttempt.confirmationCandidate === null &&
          validAttempt.confirmationInvalidated === false &&
          typeof validAttempt.errorMessage === 'string' &&
          validAttempt.rejectedCanonicalDigest === null
        );
      }
      return (
        validAttempt.confirmationCandidate === null &&
        validAttempt.confirmationInvalidated === false &&
        (validAttempt.errorMessage === undefined ||
          validAttempt.errorMessage === null ||
          typeof validAttempt.errorMessage === 'string') &&
        validAttempt.pendingAppointmentIds.length === 0 &&
        validAttempt.rejectedCanonicalDigest === null
      );
    case 'submitting':
      return (
        validAttempt.confirmationCandidate === null &&
        validAttempt.confirmationInvalidated === false &&
        (validAttempt.errorMessage === undefined ||
          validAttempt.errorMessage === null) &&
        validAttempt.pendingAppointmentIds.length === 0 &&
        validAttempt.pendingOperationId === null &&
        validAttempt.rejectedCanonicalDigest === null
      );
    case 'reconciling':
      return (
        validAttempt.confirmationCandidate === null &&
        validAttempt.confirmationInvalidated === false &&
        validAttempt.pendingAppointmentIds.length === 0 &&
        validAttempt.pendingOperationId === null &&
        validAttempt.rejectedCanonicalDigest === null
      );
    default:
      return false;
  }
};

interface BookingAttemptStore {
  attempts: StoredBookingAttempt[];
}

const isBookingAttemptStore = (
  value: unknown,
): value is BookingAttemptStore => {
  if (value === null || typeof value !== 'object') return false;
  const { attempts } = value as Partial<BookingAttemptStore>;
  return (
    Array.isArray(attempts) &&
    attempts.length <= MAX_STORED_BOOKING_ATTEMPTS &&
    attempts.every(isStoredBookingAttempt) &&
    new Set(attempts.map((attempt) => attempt.routeKey)).size ===
      attempts.length
  );
};

const isUnsafeLegacyConfirmation = (value: unknown) => {
  if (value === null || typeof value !== 'object') return false;
  const attempt = value as Partial<StoredBookingAttempt>;
  return attempt.status === 'confirmed' && attempt.pendingOperationId === null;
};

const readBookingAttemptStore = (): BookingAttemptStore => {
  const stored = window.sessionStorage.getItem(BOOKING_ATTEMPT_STORAGE_KEY);
  if (!stored) return { attempts: [] };
  const parsed: unknown = JSON.parse(stored);
  if (parsed !== null && typeof parsed === 'object') {
    const { attempts } = parsed as Partial<BookingAttemptStore>;
    if (Array.isArray(attempts)) {
      const retainedAttempts = attempts.filter(
        (attempt) => !isUnsafeLegacyConfirmation(attempt),
      );
      if (retainedAttempts.length !== attempts.length) {
        const sanitizedStore = { attempts: retainedAttempts };
        if (isBookingAttemptStore(sanitizedStore)) {
          if (retainedAttempts.length === 0) {
            window.sessionStorage.removeItem(BOOKING_ATTEMPT_STORAGE_KEY);
          } else {
            window.sessionStorage.setItem(
              BOOKING_ATTEMPT_STORAGE_KEY,
              JSON.stringify(sanitizedStore),
            );
          }
          return sanitizedStore;
        }
      }
    }
  }
  if (!isBookingAttemptStore(parsed)) {
    window.sessionStorage.removeItem(BOOKING_ATTEMPT_STORAGE_KEY);
    return { attempts: [] };
  }
  return parsed;
};

const writeBookingAttemptStore = (store: BookingAttemptStore): void => {
  window.sessionStorage.setItem(
    BOOKING_ATTEMPT_STORAGE_KEY,
    JSON.stringify(store),
  );
};

export const loadBookingAttempt = (
  routeKey: string,
): StoredBookingAttempt | null => {
  try {
    const store = readBookingAttemptStore();
    const attempt = store.attempts.find(
      (storedAttempt) => storedAttempt.routeKey === routeKey,
    );
    if (!attempt) return null;
    return attempt;
  } catch {
    try {
      window.sessionStorage.removeItem(BOOKING_ATTEMPT_STORAGE_KEY);
    } catch {
      // Storage failures fall back to the in-memory attempt.
    }
    return null;
  }
};

export const saveBookingAttempt = (attempt: StoredBookingAttempt): boolean => {
  try {
    const store = readBookingAttemptStore();
    const existingIndex = store.attempts.findIndex(
      (storedAttempt) => storedAttempt.routeKey === attempt.routeKey,
    );
    if (existingIndex >= 0) {
      store.attempts[existingIndex] = attempt;
    } else if (store.attempts.length < MAX_STORED_BOOKING_ATTEMPTS) {
      store.attempts.push(attempt);
    } else {
      const evictableIndex = store.attempts.findIndex(
        (storedAttempt) => storedAttempt.status === 'confirmed',
      );
      if (evictableIndex < 0) return false;
      store.attempts.splice(evictableIndex, 1);
      store.attempts.push(attempt);
    }
    writeBookingAttemptStore(store);
    return true;
  } catch {
    // The live page can continue even when storage is unavailable.
    return false;
  }
};

export const transitionBookingAttempt = (
  expected: StoredBookingAttempt,
  next: StoredBookingAttempt,
): boolean => {
  try {
    const store = readBookingAttemptStore();
    const attemptIndex = store.attempts.findIndex(
      (attempt) => attempt.routeKey === expected.routeKey,
    );
    const current = store.attempts[attemptIndex];
    if (
      !current ||
      current.canonicalDigest !== expected.canonicalDigest ||
      current.generation !== expected.generation ||
      current.idempotencyKey !== expected.idempotencyKey ||
      current.status !== expected.status ||
      next.canonicalDigest !== expected.canonicalDigest ||
      next.generation !== expected.generation ||
      next.idempotencyKey !== expected.idempotencyKey ||
      next.routeKey !== expected.routeKey
    ) {
      return false;
    }
    store.attempts[attemptIndex] = next;
    writeBookingAttemptStore(store);
    notifyReceiptOwner(next.routeKey);
    return true;
  } catch {
    return false;
  }
};

export const clearBookingAttempt = (
  routeKey: string,
  expectedStatus: 'confirmed' | 'submitting' = 'confirmed',
): void => {
  try {
    const store = readBookingAttemptStore();
    const attemptIndex = store.attempts.findIndex(
      (attempt) => attempt.routeKey === routeKey,
    );
    if (
      attemptIndex < 0 ||
      store.attempts[attemptIndex]?.status !== expectedStatus
    ) {
      return;
    }
    store.attempts.splice(attemptIndex, 1);
    if (store.attempts.length === 0) {
      window.sessionStorage.removeItem(BOOKING_ATTEMPT_STORAGE_KEY);
    } else {
      writeBookingAttemptStore(store);
    }
  } catch {
    // Navigation must not fail because storage is unavailable.
  }
};

// Locates the viewer's own pending attempt for a given admitted operation.
// Both the booking route and Upcoming follow the same admitted operation to
// confirmation; Upcoming uses this so it can resume a pending booking the
// member started elsewhere without relying on transient router state. The
// route key is scoped `${coachId}:${memberId}` (member) or
// `${viewerId}:${coachId}:${memberId}` (admin), so viewer ownership is proven
// by matching the member segment to the viewer id.
export const loadPendingBookingAttemptByOperationId = (
  viewerId: string,
  operationId: string,
): StoredBookingAttempt | null => {
  if (!viewerId || !operationId) return null;
  try {
    const store = readBookingAttemptStore();
    const attempt = store.attempts.find(
      (storedAttempt) =>
        storedAttempt.status === 'pending' &&
        storedAttempt.pendingOperationId === operationId &&
        storedAttempt.routeKey.split(':').pop() === viewerId,
    );
    return attempt ?? null;
  } catch {
    return null;
  }
};

// Finds the viewer's single pending attempt (if any) so Upcoming can resume an
// escaped booking without router state. A viewer never has more than one
// pending route attempt at a time.
export const findViewerPendingBookingAttempt = (
  viewerId: string,
): StoredBookingAttempt | null => {
  if (!viewerId) return null;
  try {
    const store = readBookingAttemptStore();
    const attempt = store.attempts.find(
      (storedAttempt) =>
        storedAttempt.status === 'pending' &&
        storedAttempt.pendingOperationId !== null &&
        storedAttempt.routeKey.split(':').pop() === viewerId,
    );
    return attempt ?? null;
  } catch {
    return null;
  }
};

// Consumes a followed pending attempt exactly once after its operation reaches
// a terminal result, whether it confirmed or was rejected.
export const clearPendingBookingAttempt = (routeKey: string): void => {
  try {
    const store = readBookingAttemptStore();
    const attemptIndex = store.attempts.findIndex(
      (attempt) =>
        attempt.routeKey === routeKey && attempt.status === 'pending',
    );
    if (attemptIndex < 0) return;
    store.attempts.splice(attemptIndex, 1);
    if (store.attempts.length === 0) {
      window.sessionStorage.removeItem(BOOKING_ATTEMPT_STORAGE_KEY);
    } else {
      writeBookingAttemptStore(store);
    }
  } catch {
    // Followers tolerate storage failure; the operation stays authoritative.
  }
};

export const canonicalBookingDigest = (parts: {
  cadenceWeeks: number;
  coachId: string;
  memberId: string;
  occurrenceCount: number;
  startAt: string;
}): string => {
  const canonical = [
    parts.coachId,
    parts.memberId,
    parts.cadenceWeeks,
    parts.occurrenceCount,
    parts.startAt,
  ];
  return `booking-v2:${JSON.stringify(canonical)}`;
};

const appointmentStart = (appointment: {
  currentSchedule: { startAt: string };
}) => Date.parse(appointment.currentSchedule.startAt);

const localScheduleParts = (startAt: string, timezone: string) => {
  const instant = new Date(startAt);
  if (Number.isNaN(instant.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      year: 'numeric',
    }).formatToParts(instant);
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value);
    return {
      day: value('day'),
      hour: value('hour'),
      millisecond: instant.getUTCMilliseconds(),
      minute: value('minute'),
      month: value('month'),
      second: value('second'),
      year: value('year'),
    };
  } catch {
    return null;
  }
};

const hasLocalCalendarCadence = (
  previousStartAt: string,
  currentStartAt: string,
  timezone: string,
  cadenceWeeks: number,
) => {
  const previous = localScheduleParts(previousStartAt, timezone);
  const current = localScheduleParts(currentStartAt, timezone);
  if (!previous || !current) return false;
  const previousDate = Date.UTC(
    previous.year,
    previous.month - 1,
    previous.day,
  );
  const currentDate = Date.UTC(current.year, current.month - 1, current.day);
  return (
    currentDate - previousDate === cadenceWeeks * 7 * 24 * 60 * 60 * 1000 &&
    current.hour === previous.hour &&
    current.minute === previous.minute &&
    current.second === previous.second &&
    current.millisecond === previous.millisecond
  );
};

export const confirmationCandidateFromMutation = ({
  cadenceWeeks,
  calendarEventTitle,
  durationMinutes,
  expectedCoachPersonId,
  expectedFirstStart,
  expectedMemberPersonId,
  expectedOccurrenceCount,
  expectedSeatType,
  memberEmail,
  result,
}: {
  cadenceWeeks: number;
  calendarEventTitle?: string;
  durationMinutes?: number;
  expectedCoachPersonId: string;
  expectedFirstStart: string;
  expectedMemberPersonId: string;
  expectedOccurrenceCount: number;
  expectedSeatType: 'executive' | 'leadership';
  memberEmail: string;
  result: {
    appointments: (
      | DecodedBrowserAppointment
      | ParticipantAppointment
      | ParticipantAppointmentDetail
    )[];
    contractVersion?: string;
    operationId?: string;
    rejection?: unknown;
    status?: string;
  };
}): BookingConfirmationCandidate | null => {
  if (result.appointments.length !== expectedOccurrenceCount) return null;
  const appointments = [...result.appointments].sort(
    (left, right) => appointmentStart(left) - appointmentStart(right),
  );
  const first = appointments[0];
  if (!first || first.seriesId === null || first.kind !== 'coaching')
    return null;
  if (
    first.expectedIdentities.coach.personId !== expectedCoachPersonId ||
    first.expectedIdentities.member.personId !== expectedMemberPersonId ||
    appointmentStart(first) !== Date.parse(expectedFirstStart) ||
    first.seatType !== expectedSeatType
  ) {
    return null;
  }
  const appointmentIds = new Set<string>();
  const valid = appointments.every((appointment, index) => {
    appointmentIds.add(appointment.id);
    return (
      appointment.kind === 'coaching' &&
      appointment.seriesId === first.seriesId &&
      appointment.seriesMutationVersion > 0 &&
      appointment.expectedIdentities.coach.personId === expectedCoachPersonId &&
      appointment.expectedIdentities.member.personId ===
        expectedMemberPersonId &&
      appointment.seatType === expectedSeatType &&
      appointment.currentSchedule.timezone === first.currentSchedule.timezone &&
      (index === 0 ||
        hasLocalCalendarCadence(
          appointments[index - 1]!.currentSchedule.startAt,
          appointment.currentSchedule.startAt,
          first.currentSchedule.timezone,
          cadenceWeeks,
        ))
    );
  });
  if (!valid || appointmentIds.size !== appointments.length) return null;

  return {
    appointmentIds: appointments.map((appointment) => appointment.id),
    cadenceWeeks,
    ...(calendarEventTitle ? { calendarEventTitle } : {}),
    coach: {
      avatarUrl: first.expectedIdentities.coach.avatarUrl,
      displayName: first.expectedIdentities.coach.displayName,
      personId: first.expectedIdentities.coach.personId,
    },
    ...(durationMinutes ? { durationMinutes } : {}),
    firstStart: first.currentSchedule.startAt,
    memberEmail,
    occurrenceCount: appointments.length,
    seatType: first.seatType,
    seriesId: first.seriesId,
    timezone: first.currentSchedule.timezone,
  };
};

export const confirmationCandidateFromStoredAttemptDetails = ({
  appointments,
  attempt,
  memberEmail,
}: {
  appointments: ParticipantAppointmentDetail[];
  attempt: StoredBookingAttempt;
  memberEmail: string;
}): BookingConfirmationCandidate | null => {
  if (
    attempt.transport !== 'moments' ||
    attempt.confirmationCandidate !== null ||
    attempt.pendingAppointmentIds.length !== 0 ||
    !attempt.confirmationExpectation ||
    !appointments.every(
      (appointment) => appointment.bookingStatus === 'confirmed',
    )
  ) {
    return null;
  }
  const expectation = attempt.confirmationExpectation;
  return confirmationCandidateFromMutation({
    cadenceWeeks: expectation.cadenceWeeks,
    ...(attempt.calendarEventTitle
      ? { calendarEventTitle: attempt.calendarEventTitle }
      : {}),
    expectedCoachPersonId: expectation.coachPersonId,
    expectedFirstStart: expectation.firstStart,
    expectedMemberPersonId: expectation.memberPersonId,
    expectedOccurrenceCount: expectation.occurrenceCount,
    expectedSeatType: expectation.seatType,
    memberEmail,
    result: { appointments },
  });
};

// Validates a confirmed operation's admitted appointments against the stored
// candidate's identity, series, count, cadence, and schedules. Reuses the
// shared `confirmationCandidateFromMutation` projection so the booking and
// Upcoming routes accept exactly the same confirmations.
export const confirmationMatchesStoredCandidate = (
  candidate: BookingConfirmationCandidate,
  appointments: (
    | DecodedBrowserAppointment
    | ParticipantAppointment
    | ParticipantAppointmentDetail
  )[],
): boolean => {
  // Viewer ownership already proved the member identity (route-key scope), so
  // reuse the first admitted appointment's member id and let the shared
  // projection enforce coach, seat, series, cadence, count, and schedules.
  const [first] = appointments;
  if (!first) return false;
  const reconstructed = confirmationCandidateFromMutation({
    cadenceWeeks: candidate.cadenceWeeks,
    ...(candidate.calendarEventTitle
      ? { calendarEventTitle: candidate.calendarEventTitle }
      : {}),
    ...(candidate.durationMinutes
      ? { durationMinutes: candidate.durationMinutes }
      : {}),
    expectedCoachPersonId: candidate.coach.personId,
    expectedFirstStart: candidate.firstStart,
    expectedMemberPersonId: first.expectedIdentities.member.personId,
    expectedOccurrenceCount: candidate.occurrenceCount,
    expectedSeatType: candidate.seatType,
    memberEmail: candidate.memberEmail,
    result: {
      appointments,
      contractVersion: 'appointment-mutation.v1',
      operationId: 'escaped-confirmation',
      rejection: null,
      status: 'confirmed',
    },
  });
  return (
    reconstructed !== null &&
    reconstructed.seriesId === candidate.seriesId &&
    reconstructed.appointmentIds.length === candidate.appointmentIds.length &&
    reconstructed.appointmentIds.every((id) =>
      candidate.appointmentIds.includes(id),
    )
  );
};
